import { API_URL } from '../config';
import { clearClientAuthStorage, readAuthTokens, writeAuthTokens } from './clientAuthStorage';
import { isAccessExpired, shouldRefreshSoon } from '../utils/authTokenUtils';

// ===============================
// INTERFACES BÁSICAS DE SEGURIDAD
// ===============================

export interface User {
  id: number;
  nombre?: string;
  apellido?: string;
  nombreCompleto?: string; // Campo calculado para mostrar el nombre formateado
  mail?: string;
  alias?: string;
  fotoArchivoId?: number;
  roleId: number;
  role?: Role;
  isActive: boolean;
  activo?: boolean;
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  isSystem?: boolean;
  priority?: number;
  permissions?: Permission[];
  activo?: boolean;
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

export interface Permission {
  id: number;
  name: string;
  description: string;
  resource: string;
  action: string;
  scope: string;
  module: string;
  isSystem: boolean;
  appPermissions?: AppPermission[];
  rolePermissions?: RolePermission[];
  activo: boolean;
  fechaCreacion?: string;
  fechaUpdate?: string;
  usuarioCreacion?: string;
  usuarioUpdate?: string;
}

// ===============================
// ADDITIONAL INTERFACES
// ===============================

export interface UserAppAccess {
  id: number;
  userId: number;
  appId: number;
  accessLevel: string;
  grantedBy: number;
  grantedAt: string;
}

export interface UserCenterAppAccess {
  id: number;
  userId: number;
  businessCenterId: number;
  appId: number;
  accessLevel: string;
  isDefault: boolean;
  grantedBy: number;
  grantedAt: string;
}

export interface RolePermission {
  id: number;
  roleId: number;
  permissionId: number;
  role?: Role;
  permission?: Permission;
}

export interface RoleAppAccess {
  id: number;
  roleId: number;
  appId: number;
  accessLevel: string;
  role?: Role;
}

export interface RoleCenterAccess {
  id: number;
  roleId: number;
  businessCenterId: number;
  accessLevel: string;
  isDefault: boolean;
  role?: Role;
}

export interface AppPermission {
  id: number;
  appId: number;
  permissionId: number;
  app?: App;
  permission?: Permission;
}

export interface App {
  id: number;
  name: string;
  code: string;
  type: string;
  platform?: string;
  active: boolean;
  version: string;
  userAppAccesses?: UserAppAccess[];
  appPermissions?: AppPermission[];
}

// ===============================
// REQUEST/RESPONSE INTERFACES
// ===============================

export interface CreateUserRequest {
  nombre: string;
  apellido: string;
  mail: string;
  alias: string;
  clave: string;
  roleId: number;
}

export interface UpdateUserRequest {
  id: number;
  nombre?: string;
  apellido?: string;
  mail?: string;
  alias?: string;
  roleId?: number;
  activo?: boolean;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  isSystem: boolean;
  priority: number;
  permissionIds?: number[];
}

export interface UpdateRoleRequest {
  id: number;
  name?: string;
  description?: string;
  isSystem?: boolean;
  priority?: number;
  permissionIds?: number[];
  activo?: boolean;
}

export interface CreatePermissionRequest {
  name: string;
  description: string;
  resource: string;
  action: string;
  scope: string;
  isSystem: boolean;
}

export interface UpdatePermissionRequest {
  id: number;
  name?: string;
  description?: string;
  resource?: string;
  action?: string;
  scope?: string;
  isSystem?: boolean;
  activo?: boolean;
}

// ===============================
// AUTH RESPONSE INTERFACES
// ===============================

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  user: User;
  accessibleApps: number[];
  accessibleCenters: number[];
  message?: string;
}

export interface TokenRefreshResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AppLoginRequest {
  username: string;
  password: string;
  appId: number;
}

// ===============================
// SECURITY SERVICE CLASS
// ===============================

class SecurityService {
  private refreshInFlight: Promise<boolean> | null = null;

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      'Content-Type': 'application/json',
    };
  }

  hasStoredSession(): boolean {
    const { accessToken, refreshToken, expiresAt } = readAuthTokens();
    if (refreshToken) return true;
    if (!accessToken) return false;
    return !isAccessExpired(expiresAt, accessToken);
  }

  private parseTokenRefreshPayload(data: unknown): TokenRefreshResponse | null {
    if (!data || typeof data !== 'object') return null;
    const raw = data as Record<string, unknown>;
    const token = raw.token ?? raw.Token;
    const refreshToken = raw.refreshToken ?? raw.RefreshToken;
    const expiresIn = raw.expiresIn ?? raw.ExpiresIn;
    if (!token || !refreshToken) return null;
    return {
      token: String(token),
      refreshToken: String(refreshToken),
      expiresIn: typeof expiresIn === 'number' ? expiresIn : Number(expiresIn) || 7200,
    };
  }

  private applyTokenRefresh(refreshed: TokenRefreshResponse): void {
    writeAuthTokens({
      token: refreshed.token,
      refreshToken: refreshed.refreshToken,
      expiresIn: refreshed.expiresIn,
    });
  }

  async refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefreshSession();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async doRefreshSession(): Promise<boolean> {
    const { accessToken, refreshToken } = readAuthTokens();

    try {
      if (refreshToken) {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        const parsed = this.parseTokenRefreshPayload(data);
        if (!parsed) return false;
        this.applyTokenRefresh(parsed);
        return true;
      }

      if (accessToken && !isAccessExpired(readAuthTokens().expiresAt, accessToken)) {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) return false;
        const data = await res.json();
        const parsed = this.parseTokenRefreshPayload(data);
        if (!parsed) return false;
        this.applyTokenRefresh(parsed);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error en refreshSession:', error);
      return false;
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const { accessToken, refreshToken, expiresAt } = readAuthTokens();
    if (!accessToken && !refreshToken) return null;

    const needsRefresh =
      !accessToken ||
      isAccessExpired(expiresAt, accessToken) ||
      shouldRefreshSoon(expiresAt, accessToken);

    if (!needsRefresh && accessToken) {
      return accessToken;
    }

    const ok = await this.refreshSession();
    if (!ok) return null;
    return readAuthTokens().accessToken;
  }

  /**
   * Unifica JSON camelCase / PascalCase del login o de GET /api/users/:id.
   */
  parseUserFromApi(raw: any): User | null {
    if (!raw || typeof raw !== 'object') return null;
    const roleRaw = raw.role ?? raw.Role;
    let role: Role | undefined;
    if (roleRaw && typeof roleRaw === 'object') {
      role = {
        id: roleRaw.id ?? roleRaw.Id,
        name: String(roleRaw.name ?? roleRaw.Name ?? ''),
        description: String(roleRaw.description ?? roleRaw.Description ?? ''),
        isSystem: roleRaw.isSystem ?? roleRaw.IsSystem,
        priority: roleRaw.priority ?? roleRaw.Priority,
        activo: roleRaw.activo ?? roleRaw.Activo,
        fechaCreacion: roleRaw.fechaCreacion ?? roleRaw.FechaCreacion,
        fechaUpdate: roleRaw.fechaUpdate ?? roleRaw.FechaUpdate,
        usuarioCreacion: roleRaw.usuarioCreacion ?? roleRaw.UsuarioCreacion,
        usuarioUpdate: roleRaw.usuarioUpdate ?? roleRaw.UsuarioUpdate,
      };
    }
    const user: User = {
      id: raw.id ?? raw.Id,
      nombre: raw.nombre ?? raw.Nombre,
      apellido: raw.apellido ?? raw.Apellido,
      mail: raw.mail ?? raw.Mail,
      alias: raw.alias ?? raw.Alias,
      fotoArchivoId: raw.fotoArchivoId ?? raw.FotoArchivoId,
      roleId: raw.roleId ?? raw.RoleId,
      isActive: raw.isActive ?? raw.IsActive ?? true,
      activo: raw.activo ?? raw.Activo,
      fechaCreacion: raw.fechaCreacion ?? raw.FechaCreacion,
      fechaUpdate: raw.fechaUpdate ?? raw.FechaUpdate,
      usuarioCreacion: raw.usuarioCreacion ?? raw.UsuarioCreacion,
      usuarioUpdate: raw.usuarioUpdate ?? raw.UsuarioUpdate,
      role,
      nombreCompleto: '',
    };
    user.nombreCompleto = this.formatUserName(user);
    return user;
  }

  /** Persiste token, usuario y alcances tras login o GET /profile. */
  private persistAuthResponse(data: AuthResponse): void {
    writeAuthTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresIn: data.expiresIn,
    });
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('accessibleCenters', JSON.stringify(data.accessibleCenters ?? []));
    localStorage.setItem('accessibleApps', JSON.stringify(data.accessibleApps ?? []));
  }

  /**
   * Valida el token contra el API y refresca usuario + apps/centros en localStorage.
   * En 401/403 limpia sesión. En otros fallos devuelve null sin limpiar (reintento / fallback).
   */
  async fetchProfileSession(): Promise<AuthResponse | null> {
    if (!this.hasStoredSession()) return null;

    const fetchProfile = async (token: string) =>
      fetch(`${API_URL}/api/auth/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

    try {
      let token = await this.getValidAccessToken();
      if (!token) return null;

      let res = await fetchProfile(token);

      if (res.status === 401) {
        const refreshed = await this.refreshSession();
        if (!refreshed) {
          clearClientAuthStorage();
          return null;
        }
        token = readAuthTokens().accessToken;
        if (!token) {
          clearClientAuthStorage();
          return null;
        }
        res = await fetchProfile(token);
      }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearClientAuthStorage();
        }
        return null;
      }

      const data = await res.json();
      const userRaw = data.user ?? data.User;
      const user = this.parseUserFromApi(userRaw);
      if (!user) {
        clearClientAuthStorage();
        return null;
      }
      const apps = data.accessibleApps ?? data.AccessibleApps;
      const centers = data.accessibleCenters ?? data.AccessibleCenters;
      const { refreshToken, expiresAt } = readAuthTokens();
      const normalized: AuthResponse = {
        token,
        refreshToken: refreshToken ?? undefined,
        expiresIn: expiresAt
          ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
          : undefined,
        user,
        accessibleApps: Array.isArray(apps) ? apps : [],
        accessibleCenters: Array.isArray(centers) ? centers : [],
      };
      this.persistAuthResponse(normalized);
      return normalized;
    } catch (error) {
      console.error('Error en fetchProfileSession:', error);
      return null;
    }
  }

  private normalizeAuthResponsePayload(data: any): AuthResponse {
    const token = data.token ?? data.Token;
    const userRaw = data.user ?? data.User;
    if (!token || userRaw == null) {
      throw new Error('Respuesta de autenticación inválida');
    }
    const user = this.parseUserFromApi(userRaw);
    if (!user) {
      throw new Error('Usuario inválido en la respuesta de autenticación');
    }
    const apps = data.accessibleApps ?? data.AccessibleApps;
    const centers = data.accessibleCenters ?? data.AccessibleCenters;
    const refreshToken = data.refreshToken ?? data.RefreshToken;
    const expiresIn = data.expiresIn ?? data.ExpiresIn;
    return {
      token: String(token),
      refreshToken: refreshToken != null ? String(refreshToken) : undefined,
      expiresIn: expiresIn != null ? Number(expiresIn) : undefined,
      user,
      accessibleApps: Array.isArray(apps) ? apps : [],
      accessibleCenters: Array.isArray(centers) ? centers : [],
      message: data.message ?? data.Message,
    };
  }

  // ===============================
  // AUTENTICACIÓN
  // ===============================

  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      // Limpiar estado anterior para evitar mezclar permisos/alcances entre usuarios.
      clearClientAuthStorage();

      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Username: username, Password: password }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Credenciales inválidas');
      }

      const data = await res.json();
      const normalized = this.normalizeAuthResponsePayload(data);
      this.persistAuthResponse(normalized);
      return normalized;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  async loginWithApp(username: string, password: string, appId: number): Promise<AuthResponse> {
    try {
      // Limpiar estado anterior para evitar mezclar permisos/alcances entre usuarios.
      clearClientAuthStorage();

      const res = await fetch(`${API_URL}/api/auth/login-with-app`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Username: username,
          Password: password,
          AppId: appId,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.message === 'No tiene permisos para acceder a esta aplicación') {
          throw new Error('No tiene permisos para acceder a esta aplicación');
        }
        throw new Error(errorData.message || 'Credenciales inválidas');
      }

      const data = await res.json();
      const normalized = this.normalizeAuthResponsePayload(data);
      this.persistAuthResponse(normalized);
      localStorage.setItem('currentAppId', appId.toString());
      return normalized;
    } catch (error) {
      console.error('Error en login con aplicación:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error al registrar logout en el backend:', error);
    } finally {
      clearClientAuthStorage();
    }
  }

  async refreshToken(): Promise<string | null> {
    const ok = await this.refreshSession();
    if (!ok) return null;
    return readAuthTokens().accessToken;
  }

  // ===============================
  // APP MANAGEMENT
  // ===============================

  getCurrentAppId(): number | null {
    const appId = localStorage.getItem('currentAppId');
    return appId ? parseInt(appId, 10) : null;
  }

  setCurrentAppId(appId: number): void {
    localStorage.setItem('currentAppId', appId.toString());
  }

  // ===============================
  // USER MANAGEMENT
  // ===============================

  async getUsers(): Promise<User[]> {
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener usuarios');
      const users = await res.json();
      return (Array.isArray(users) ? users : [])
        .map((user: any) => this.parseUserFromApi(user))
        .filter((user): user is User => user != null && Number.isFinite(user.id));
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  }

  // Función para formatear el nombre completo del usuario
  private formatUserName(user: User): string {
    const nombre = user.nombre?.trim() || '';
    const apellido = user.apellido?.trim() || '';

    if (nombre && apellido) {
      return `${apellido}, ${nombre}`;
    } else if (apellido) {
      return apellido;
    } else if (nombre) {
      return nombre;
    } else {
      return user.alias || user.mail || 'Usuario sin nombre';
    }
  }

  async getUser(id: number): Promise<User | null> {
    try {
      const res = await fetch(`${API_URL}/api/users/${id}`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) return null;
      const raw = await res.json();
      return this.parseUserFromApi(raw);
    } catch (error) {
      console.error('Error getting user:', error);
      return null;
    }
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    const res = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = 'Error al crear usuario';
      if (text?.trim()) {
        try {
          const j = JSON.parse(text) as { message?: string; error?: string };
          message = j.message || j.error || text;
        } catch {
          message = text;
        }
      }
      throw new Error(message);
    }
    const raw = await res.json();
    const parsed = this.parseUserFromApi(raw);
    if (!parsed) {
      throw new Error('Respuesta inválida al crear usuario');
    }
    return parsed;
  }

  async updateUser(userData: UpdateUserRequest): Promise<User | null> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No hay token de autenticación. Por favor, inicia sesión nuevamente.');
      }

      const url = `${API_URL}/api/users/${userData.id}`;
      console.log('Updating user:', url, 'Token present:', !!token);

      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!res.ok) {
        let errorMessage = 'Error al actualizar usuario';

        // Si es 401, el token puede haber expirado
        if (res.status === 401) {
          errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
          clearClientAuthStorage();
        } else {
          try {
            const errorText = await res.text();
            if (errorText) {
              try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.message || errorJson.error || errorMessage;
              } catch {
                // Si no es JSON, usar el texto como mensaje
                errorMessage = errorText || errorMessage;
              }
            }
          } catch {
            // Si no se puede parsear el error, usar el mensaje por defecto
          }
        }

        throw new Error(errorMessage);
      }

      // Si la respuesta es 204 (NoContent), el backend no devuelve cuerpo
      // Obtener el usuario actualizado desde el servidor
      if (res.status === 204) {
        try {
          const updatedUser = await this.getUser(userData.id);
          if (updatedUser) {
            return updatedUser;
          }
        } catch (error) {
          console.warn('No se pudo obtener el usuario actualizado:', error);
        }

        // Fallback: devolver los datos que enviamos
        return {
          id: userData.id,
          nombre: userData.nombre,
          apellido: userData.apellido,
          mail: userData.mail,
          alias: userData.alias,
          roleId: userData.roleId || 0,
          activo: true,
        } as User;
      }

      // Si hay contenido, intentar parsearlo
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await res.text();
        if (text && text.trim()) {
          try {
            return JSON.parse(text);
          } catch (parseError) {
            console.warn('Error parseando respuesta JSON:', parseError);
          }
        }
      }

      // Fallback final: obtener el usuario actualizado
      return await this.getUser(userData.id);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(id: number): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      return res.ok;
    } catch (error) {
      console.error('Error deleting user:', error);
      return false;
    }
  }

  // ===============================
  // ROLE MANAGEMENT
  // ===============================

  async getRoles(): Promise<Role[]> {
    try {
      const res = await fetch(`${API_URL}/api/roles`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener roles');
      return res.json();
    } catch (error) {
      console.error('Error getting roles:', error);
      return [];
    }
  }

  async getRole(id: number): Promise<Role | null> {
    try {
      // Usar el endpoint GET /api/roles/{id} que ahora incluye los permisos
      const res = await fetch(`${API_URL}/api/roles/${id}`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) {
        if (res.status === 404) return null;
        throw new Error('Error al obtener el rol');
      }
      return await res.json();
    } catch (error) {
      console.error('Error getting role:', error);
      return null;
    }
  }

  async createRole(roleData: CreateRoleRequest): Promise<Role> {
    const res = await fetch(`${API_URL}/api/roles`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(roleData),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = 'Error al crear rol';
      if (text?.trim()) {
        try {
          const j = JSON.parse(text) as { message?: string; error?: string };
          message = j.message || j.error || text;
        } catch {
          message = text;
        }
      }
      throw new Error(message);
    }
    return (await res.json()) as Role;
  }

  async updateRole(roleData: UpdateRoleRequest): Promise<Role | null> {
    try {
      const res = await fetch(`${API_URL}/api/roles/${roleData.id}`, {
        method: 'PUT',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roleData),
      });

      if (!res.ok) {
        let errorMessage = 'Error al actualizar rol';
        try {
          const contentType = res.headers.get('content-type');
          let errorText = '';
          if (contentType && contentType.includes('application/json')) {
            errorText = await res.text();
            if (errorText && errorText.trim()) {
              const error = JSON.parse(errorText);
              errorMessage = error.message || error.error || error.details || errorMessage;
              console.error('Error del backend:', error);
            }
          } else {
            errorText = await res.text();
            if (errorText && errorText.trim()) {
              errorMessage = errorText;
            }
          }
          console.error(`Error ${res.status} al actualizar rol:`, errorMessage);
        } catch (parseError) {
          console.error('Error parseando respuesta de error:', parseError);
        }
        throw new Error(errorMessage);
      }

      // Si la respuesta es 204 NoContent, obtener el rol actualizado
      if (res.status === 204) {
        // Obtener el rol actualizado
        return await this.getRole(roleData.id);
      }

      // Si hay contenido JSON, parsearlo
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await res.text();
        if (text && text.trim()) {
          return JSON.parse(text);
        }
      }

      // Si no hay contenido, obtener el rol actualizado
      return await this.getRole(roleData.id);
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  async deleteRole(id: number): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/roles/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      return res.ok;
    } catch (error) {
      console.error('Error deleting role:', error);
      return false;
    }
  }

  // ===============================
  // PERMISSION MANAGEMENT
  // ===============================

  async getPermissions(): Promise<Permission[]> {
    try {
      const res = await fetch(`${API_URL}/api/permissions`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener permisos');
      return res.json();
    } catch (error) {
      console.error('Error getting permissions:', error);
      return [];
    }
  }

  async getPermission(id: number): Promise<Permission | null> {
    try {
      // Como el backend no tiene endpoint individual, obtenemos todos y filtramos
      const permissions = await this.getPermissions();
      return permissions.find((p) => p.id === id) || null;
    } catch (error) {
      console.error('Error getting permission:', error);
      return null;
    }
  }

  async createPermission(permissionData: CreatePermissionRequest): Promise<Permission> {
    const res = await fetch(`${API_URL}/api/permissions`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(permissionData),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message = 'Error al crear permiso';
      if (text?.trim()) {
        try {
          const j = JSON.parse(text) as { message?: string; error?: string };
          message = j.message || j.error || text;
        } catch {
          message = text;
        }
      }
      throw new Error(message);
    }
    return (await res.json()) as Permission;
  }

  async updatePermission(permissionData: UpdatePermissionRequest): Promise<Permission | null> {
    const res = await fetch(`${API_URL}/api/permissions/${permissionData.id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(permissionData),
    });

    if (!res.ok) {
      let errorMessage = 'Error al actualizar permiso';
      try {
        const errorText = await res.text();
        if (errorText?.trim()) {
          try {
            const err = JSON.parse(errorText) as { message?: string; error?: string };
            errorMessage = err.message || err.error || errorText;
          } catch {
            errorMessage = errorText;
          }
        }
      } catch {
        /* ignore */
      }
      throw new Error(errorMessage);
    }

    if (res.status === 204) {
      return await this.getPermission(permissionData.id);
    }

    const contentType = res.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const text = await res.text();
      if (text?.trim()) {
        return JSON.parse(text) as Permission;
      }
    }

    return await this.getPermission(permissionData.id);
  }

  async deletePermission(id: number): Promise<boolean> {
    try {
      const res = await fetch(`${API_URL}/api/permissions/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      return res.ok;
    } catch (error) {
      console.error('Error deleting permission:', error);
      return false;
    }
  }

  // ===============================
  // MÉTODOS ADICIONALES PARA CENTROS Y CLIENTES
  // ===============================

  async getUsuarioCentros(id: number): Promise<any[]> {
    try {
      const res = await fetch(`${API_URL}/api/users/${id}/centers`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener centros del usuario');
      const data = await res.json();
      // Normalizar los datos para que coincidan con el formato esperado
      return (data || []).map((center: any) => ({
        id: center.id,
        nombre: center.name || center.nombre || center.Name || '',
        descripcion: center.description || center.descripcion || center.Description || '',
        activo: center.activo ?? center.active ?? center.Activo ?? true,
      }));
    } catch (error) {
      console.error('Error getting usuario centros:', error);
      return [];
    }
  }

  async getUsuarioClientes(id: number): Promise<any[]> {
    try {
      const res = await fetch(`${API_URL}/api/users/${id}/clients`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener clientes del usuario');
      const data = await res.json();
      // Normalizar los datos para que coincidan con el formato esperado
      return (data || []).map((cliente: any) => ({
        id: cliente.id,
        nombre: cliente.nombre || cliente.Nombre || cliente.name || '',
        cuit: cliente.cuit || cliente.CUIT || cliente.cuit || '',
        centro:
          cliente.businessCenter || cliente.centro || cliente.Centro
            ? {
                id: (cliente.businessCenter || cliente.centro || cliente.Centro).id,
                nombre:
                  (cliente.businessCenter || cliente.centro || cliente.Centro).name ||
                  (cliente.businessCenter || cliente.centro || cliente.Centro).nombre ||
                  (cliente.businessCenter || cliente.centro || cliente.Centro).Name ||
                  '',
              }
            : null,
        activo: cliente.activo ?? cliente.Activo ?? cliente.active ?? true,
      }));
    } catch (error) {
      console.error('Error getting usuario clientes:', error);
      return [];
    }
  }

  async assignCenterToUser(
    userId: number,
    centerId: number,
    appId?: number,
    accessLevel: string = 'full',
    isDefault: boolean = false,
  ): Promise<any> {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/centers`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          centerId,
          appId,
          accessLevel,
          isDefault,
        }),
      });

      // Leer el contenido de la respuesta antes de verificar res.ok
      // para evitar errores de "body already read"
      const contentType = res.headers.get('content-type');
      let responseText = '';
      let responseData: any = null;

      if (contentType && contentType.includes('application/json')) {
        try {
          responseText = await res.text();
          if (responseText && responseText.trim()) {
            responseData = JSON.parse(responseText);
          }
        } catch (parseError) {
          console.warn('Error parseando respuesta JSON:', parseError);
        }
      }

      if (!res.ok) {
        let errorMessage = 'Error al asignar centro al usuario';
        if (responseData) {
          // Priorizar el mensaje detallado si está disponible
          errorMessage = responseData.details
            ? `${responseData.message || errorMessage}: ${responseData.details}`
            : responseData.message || responseData.error || errorMessage;
        } else if (responseText) {
          errorMessage = responseText;
        }
        console.error('Error response:', {
          status: res.status,
          statusText: res.statusText,
          data: responseData,
          text: responseText,
        });
        throw new Error(errorMessage);
      }

      // Si la respuesta fue exitosa, devolver los datos parseados o un objeto de éxito
      if (responseData) {
        return responseData;
      }

      // Si no hay contenido JSON pero la respuesta fue exitosa, devolver éxito
      return { message: 'Centro asignado correctamente', success: true };
    } catch (error) {
      console.error('Error assigning center to user:', error);
      throw error;
    }
  }

  async getCenterClients(centerId: number, q?: string): Promise<any[]> {
    try {
      const term = (q || '').trim();
      if (term.length < 3) {
        return [];
      }

      const params = new URLSearchParams({ q: term });
      const res = await fetch(
        `${API_URL}/api/users/centers/${centerId}/clients?${params.toString()}`,
        {
          headers: this.getAuthHeaders(),
        },
      );
      if (!res.ok) throw new Error('Error al obtener clientes del centro');
      const data = await res.json();
      return (data || []).map((cliente: any) => ({
        id: cliente.id,
        nombre: cliente.nombre || cliente.Nombre || cliente.name || '',
        cuit: cliente.cuit || cliente.CUIT || '',
        businessCenterId: cliente.businessCenterId || cliente.BusinessCenterId,
        activo: cliente.activo ?? cliente.Activo ?? cliente.active ?? true,
      }));
    } catch (error) {
      console.error('Error getting center clients:', error);
      return [];
    }
  }

  async getUserAssignedClients(userId: number): Promise<any[]> {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/assigned-clients`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener clientes asignados del usuario');
      const data = await res.json();
      return (data || []).map((cliente: any) => {
        const bc = cliente.businessCenter || cliente.BusinessCenter;
        return {
          id: cliente.id,
          nombre: cliente.nombre || cliente.Nombre || cliente.name || '',
          cuit: cliente.cuit || cliente.CUIT || '',
          businessCenterId: cliente.businessCenterId || cliente.BusinessCenterId,
          centro: bc
            ? {
                id: bc.id,
                nombre: bc.name || bc.nombre || bc.Name || '',
              }
            : null,
          activo: cliente.activo ?? cliente.Activo ?? cliente.active ?? true,
        };
      });
    } catch (error) {
      console.error('Error getting user assigned clients:', error);
      return [];
    }
  }

  async assignClientToUser(
    userId: number,
    clienteId: number,
    appId?: number,
    accessLevel: string = 'full',
  ): Promise<any> {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/clients`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clienteId,
          appId,
          accessLevel,
        }),
      });

      const contentType = res.headers.get('content-type');
      let responseText = '';
      let responseData: any = null;

      if (contentType && contentType.includes('application/json')) {
        try {
          responseText = await res.text();
          if (responseText && responseText.trim()) {
            responseData = JSON.parse(responseText);
          }
        } catch (parseError) {
          console.warn('Error parseando respuesta JSON:', parseError);
        }
      }

      if (!res.ok) {
        let errorMessage = 'Error al asignar cliente al usuario';
        if (responseData) {
          if (responseData.details) {
            errorMessage = `${responseData.message || errorMessage}: ${responseData.details}`;
          } else {
            errorMessage = responseData.message || responseData.error || errorMessage;
          }
        } else if (responseText) {
          errorMessage = responseText;
        }
        console.error('Error response:', {
          status: res.status,
          statusText: res.statusText,
          data: responseData,
          text: responseText,
        });
        throw new Error(errorMessage);
      }

      if (responseData) {
        return responseData;
      }

      return { message: 'Cliente asignado correctamente', success: true };
    } catch (error) {
      console.error('Error assigning client to user:', error);
      throw error;
    }
  }

  async removeClientFromUser(userId: number, clienteId: number, appId?: number): Promise<void> {
    try {
      const url = appId
        ? `${API_URL}/api/users/${userId}/clients/${clienteId}?appId=${appId}`
        : `${API_URL}/api/users/${userId}/clients/${clienteId}`;

      const res = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Error al desasignar cliente del usuario');
      }
    } catch (error) {
      console.error('Error removing client from user:', error);
      throw error;
    }
  }

  async removeCenterFromUser(userId: number, centerId: number, appId?: number): Promise<void> {
    try {
      const url = appId
        ? `${API_URL}/api/users/${userId}/centers/${centerId}?appId=${appId}`
        : `${API_URL}/api/users/${userId}/centers/${centerId}`;

      const res = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Error al desasignar centro del usuario');
      }
    } catch (error) {
      console.error('Error removing center from user:', error);
      throw error;
    }
  }

  async getUserPermissions(id: number): Promise<Permission[]> {
    try {
      const res = await fetch(`${API_URL}/api/permissions/user/${id}`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener permisos del usuario');
      return res.json();
    } catch (error) {
      console.error('Error getting user permissions:', error);
      return [];
    }
  }

  async getMyPermissions(): Promise<Permission[]> {
    try {
      const res = await fetch(`${API_URL}/api/useraccess/my-permissions`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener mis permisos');
      const data = await res.json();
      // Convertir el formato del backend al formato del frontend
      return data.map((p: any) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        resource: p.resource,
        action: p.action,
        scope: p.scope,
        isSystem: p.isSystem,
        activo: true,
        fechaCreacion: new Date().toISOString(),
        fechaUpdate: null,
        usuarioCreacion: 'system',
        usuarioUpdate: null,
      }));
    } catch (error) {
      console.error('Error getting my permissions:', error);
      return [];
    }
  }

  async getRolePermissions(id: number): Promise<Permission[]> {
    try {
      const res = await fetch(`${API_URL}/api/roles/${id}/permissions`, {
        headers: this.getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error al obtener permisos del rol');
      return res.json();
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }
}

export const securityService = new SecurityService();
